/**
 * Wave 29 Chunk 9b — Position detail + ranked candidates.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Briefcase, ChevronLeft, Edit3, Star, RotateCw, Loader2, Trash2, ChevronRight, UserPlus,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";
import BulkCvUploadButton from "../components/recruitment/BulkCvUploadButton";

const RECO_COLORS = {
  shortlist: "bg-emerald-500/20 text-emerald-300",
  consider: "bg-amber-500/20 text-amber-300",
  pass: "bg-rose-500/20 text-rose-300",
};

export default function RecruitmentPositionDetail() {
  const { user } = useAuth();
  const isHr = user && ["owner", "admin"].includes(user.role);
  const { pid } = useParams();
  const nav = useNavigate();
  const [pos, setPos] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ranking, setRanking] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/recruitment/positions/${pid}`);
      setPos(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load position.");
    } finally { setBusy(false); }
  };

  useEffect(() => { if (user && isHr) load(); /* eslint-disable-next-line */ }, [user, isHr, pid]);

  const rankAll = async () => {
    setRanking(true);
    try {
      await api.post(`/recruitment/positions/${pid}/rank`);
      toast.success("Ranking complete");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rank failed.");
    } finally { setRanking(false); }
  };

  const del = async () => {
    if (!window.confirm("Delete this position?")) return;
    try {
      await api.delete(`/recruitment/positions/${pid}`);
      toast.success("Deleted");
      nav("/recruitment");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  if (!isHr) return <div className="hv-card p-6 text-sm text-[#dde3ec]">HR only.</div>;
  if (busy && !pos) {
    return <div className="hv-card p-6 text-sm text-[#9aa3b5]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>;
  }
  if (!pos) return null;

  return (
    <div className="space-y-4 max-w-4xl" data-testid="position-detail-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to="/recruitment" className="hv-btn hv-btn-ghost text-xs" data-testid="position-detail-back">
          <ChevronLeft className="w-3.5 h-3.5" /> Back
        </Link>
        <h1 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2 text-center">
          <Briefcase className="w-5 h-5 text-[#1FB6C1]" /> {pos.title}
        </h1>
        <div className="flex items-center gap-2">
          <button onClick={() => nav(`/recruitment/positions/${pid}/edit`)} className="hv-btn hv-btn-ghost text-xs" data-testid="position-detail-edit">
            <Edit3 className="w-3.5 h-3.5" /> Edit
          </button>
          <button onClick={del} className="hv-btn hv-btn-ghost text-xs text-rose-300" data-testid="position-detail-delete">
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      </div>

      <div className="hv-card p-4 space-y-2 text-sm" data-testid="position-meta">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={"px-2 py-0.5 rounded text-[10px] font-bold " + (pos.status === "open" ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-500/20 text-zinc-300")}>
            {pos.status}
          </span>
          {pos.location && <span className="text-xs text-[#cbd5e1]">{pos.location}</span>}
          {pos.department && <span className="text-xs text-[#cbd5e1]">· {pos.department}</span>}
          {pos.employment_type && <span className="text-xs text-[#cbd5e1]">· {pos.employment_type.replace("_", " ")}</span>}
          {pos.years_min ? <span className="text-xs text-[#cbd5e1]">· {pos.years_min}+ yrs</span> : null}
          {pos.salary_range && <span className="text-xs text-amber-300">· {pos.salary_range}</span>}
        </div>
        {pos.summary && <div className="text-[#dde3ec]">{pos.summary}</div>}
        {(pos.required_skills || []).length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Required skills</div>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {pos.required_skills.map((s) => <span key={s} className="px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-200 text-[11px]">{s}</span>)}
            </div>
          </div>
        )}
        {(pos.nice_to_haves || []).length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Nice-to-haves</div>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {pos.nice_to_haves.map((s) => <span key={s} className="px-2 py-0.5 rounded bg-white/5 text-[#dde3ec] text-[11px]">{s}</span>)}
            </div>
          </div>
        )}
        {(pos.responsibilities || []).length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Responsibilities</div>
            <ul className="list-disc list-inside text-[#dde3ec] text-xs mt-1">
              {pos.responsibilities.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </div>
        )}
        {pos.hidro_notes && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Hidro notes</div>
            <div className="text-[#cbd5e1] text-xs whitespace-pre-wrap">{pos.hidro_notes}</div>
          </div>
        )}
      </div>

      <div className="hv-card overflow-hidden" data-testid="position-candidates">
        <div className="px-4 py-2.5 border-b border-white/5 flex items-center justify-between gap-2 flex-wrap">
          <h2 className="text-sm font-semibold text-white">Ranked candidates ({(pos.candidates || []).length})</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/recruitment/candidates/new?position=${pid}`} className="hv-btn hv-btn-ghost text-xs" data-testid="position-add-cv">
              <UserPlus className="w-3.5 h-3.5" /> Single CV
            </Link>
            <BulkCvUploadButton positionId={pid} onDone={load} />
            <button onClick={rankAll} disabled={ranking} className="hv-btn hv-btn-primary text-xs disabled:opacity-50" data-testid="position-rerank">
              {ranking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
              Re-rank
            </button>
          </div>
        </div>
        {(pos.candidates || []).length === 0 ? (
          <div className="p-6 text-[#9aa3b5] text-sm text-center">
            No candidates linked yet —{" "}
            <Link to={`/recruitment/candidates/new?position=${pid}`} className="text-[#1FB6C1] hover:underline">upload a CV</Link>.
          </div>
        ) : (
          <ul className="divide-y divide-white/5">
            {(pos.candidates || []).map((c) => {
              const r = c.ranking || {};
              return (
                <li key={c.id} className="px-3 sm:px-4 py-3" data-testid={`position-candidate-${c.id}`}>
                  <Link to={`/recruitment/candidates/${c.id}`} className="flex items-start gap-3 group">
                    <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center text-sm font-bold text-white shrink-0">
                      {typeof r.score === "number" ? r.score : "—"}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-white truncate">{c.full_name || "Unnamed"}</span>
                        {r.recommendation && (
                          <span className={"px-1.5 py-0.5 rounded text-[10px] " + (RECO_COLORS[r.recommendation] || "bg-white/5 text-[#dde3ec]")}>
                            {r.recommendation}
                          </span>
                        )}
                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-white/5 text-[#cbd5e1]">{c.status}</span>
                      </div>
                      {(r.fit_summary || c.parsed?.headline) && (
                        <div className="text-[12px] text-[#dde3ec] mt-1">{r.fit_summary || c.parsed?.headline}</div>
                      )}
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {(r.strengths || []).slice(0, 3).map((s, i) => (
                          <span key={`s${i}`} className="px-1.5 py-0.5 rounded text-[10px] bg-emerald-500/15 text-emerald-200">+ {s}</span>
                        ))}
                        {(r.gaps || []).slice(0, 2).map((s, i) => (
                          <span key={`g${i}`} className="px-1.5 py-0.5 rounded text-[10px] bg-amber-500/15 text-amber-200">− {s}</span>
                        ))}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#647087] group-hover:text-white shrink-0 self-center" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
